import {
  FC,
  PropsWithChildren,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { Loading } from "../../../pages";

import AuthenticationService from "../../services/authentication";

import { AuthContext } from "./AuthContext";

import { LoginUserInformation, Token, UserInformation } from "../../interfaces";

import { api } from "../../../config";

import userHelper from "../../utils/userHelper";
import localStorageHelper from "../../utils/localStorageHelper";
import { useIdleTimer } from "react-idle-timer"; // NUEVO
import {
  mapCognitoToUserInformation,
  translateCognitoError,
} from "./utils/utils";
import { getError } from "../../utils";
import { message } from "antd";
import { fixEncoding } from "../../services/insurancePolicy/utils";

export interface AuthState {
  token?: Token;
}

export const AuthProvider: FC<PropsWithChildren> = ({ children }) => {
  const initialized = useRef(false);

  const [tokens, setTokens] = useState<Record<string, Token | null> | null>(
    null
  );

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isLoadingLogin, setIsLoadingLogin] = useState<boolean>(false);

  const [userInfo, setUserInfo] = useState<null | UserInformation>(
    userHelper.getUser()
  );
  const tokensRef = useRef<Record<string, Token | null>>({});
  const scopes = process.env.REACT_APP_SCOPES?.split(",") || [];

  const refreshInterval = useRef<NodeJS.Timeout | null>(null);

  const onActive = () => {
    if (refreshInterval.current) return;

    refreshInterval.current = setInterval(async () => {
      try {
        const refreshToken = localStorageHelper.get<string>("refresh_token");
        if (!refreshToken) throw new Error("No hay refresh token");

        const session = await AuthenticationService.refreshSession(
          refreshToken
        );

        localStorageHelper.save("access_token", session.accessToken);
        localStorageHelper.save("id_token", session.idToken);
      } catch (error) {
        logout();
      }
    }, 15 * 60 * 1000);
  };

  const onIdle = () => {
    if (refreshInterval.current) {
      clearInterval(refreshInterval.current);
      refreshInterval.current = null;
    }
  };

  useIdleTimer({
    timeout: 15 * 60 * 1000,
    onIdle: onIdle,
    onActive: onActive,
    debounce: 500,
    events: ["keydown", "touchstart", "scroll", "click"],
  });

  const logout = () => {
    localStorage.removeItem("hdi.access_token");
    localStorage.removeItem("hdi.refresh_token");
    localStorage.removeItem("hdi.uid");

    AuthenticationService.cognitoLogout();

    window.location.href = "/login";

    return null;
  };

  const login = async (user: string, pass: string) => {
    try {
      setIsLoadingLogin(true);
      const loginResponse = await AuthenticationService.login(user, pass);

      localStorageHelper.save("access_token", loginResponse.accessToken);
      localStorageHelper.save("refresh_token", loginResponse.refreshToken);

      const cognitoUserData: LoginUserInformation = userHelper.parseUserJwt(
        loginResponse.idToken
      );

      const userData: UserInformation =
        mapCognitoToUserInformation(cognitoUserData);

      setUserInfo(userData);

      userHelper.saveUser(userData);

      await fetchTokens();

      return true;
    } catch (error: any) {      
      let errorMessage = "Error desconocido. Intenta nuevamente.";
      if (error.response?.data?.__type) {
        const cognitoErrorCode = error.response.data.__type;        
        errorMessage = translateCognitoError(cognitoErrorCode);
      } else if (error.code) {
        errorMessage = translateCognitoError(error.code);
      } else if (error.message) {
        errorMessage = error.message;
      }
      message.error(errorMessage);
      return false;
    } finally {
      setIsLoadingLogin(false);
    }
  };

  const getUserInfo = () => {
    return userHelper.getUser();
  };

  const updateUserInfo = (userInfo: UserInformation) => {
    setUserInfo(userInfo);

    return userHelper.saveUser(userInfo);
  };

  const fetchTokens = async () => {
    const newTokens: Record<string, Token | null> = {};
    try {
      setIsLoading(true);

      for (const scope of scopes) {
        const tokenResponse = await AuthenticationService.getToken(scope);

        newTokens[scope] = tokenResponse.data;
      }

      setTokens(newTokens);
      tokensRef.current = newTokens;

      api.interceptors.request.use(
        function (config) {
          if (config.headers["X-IBM-Client-Id"]) {
            const scope = config.headers["scope"] || "Productores";
            const token = tokensRef.current[scope]?.access_token;

            config.headers["Authorization"] = `Bearer ${token}`;
            config.headers["scope"] = scope;
          }

          return config;
        },
        function (error) {
          const normalizedError =
            error instanceof Error ? error : new Error(String(error));

          return Promise.reject(normalizedError);
        }
      );

      let refreshTokenPromise: Promise<string | null> | null = null;
      api.interceptors.response.use(
        (response) => {
          if (typeof response.data === "string") {
            response.data = fixEncoding(response.data);
          }
      
          // Si es JSON, recorrer solo los campos string
          else if (typeof response.data === "object" && response.data !== null) {
            const fixDeep = (obj: any) => {
              for (const key in obj) {
                if (typeof obj[key] === "string") {
                  obj[key] = fixEncoding(obj[key]);
                } else if (typeof obj[key] === "object" && obj[key] !== null) {
                  fixDeep(obj[key]);
                }
              }
            };
            fixDeep(response.data);
          }
      
          return response;
        },
        async (error) => {
          const originalRequest = error.config;

          if (
            error.response?.status === 401 &&
            !originalRequest._retry &&
            originalRequest.headers["X-IBM-Client-Id"]
          ) {
            originalRequest._retry = true;

            const scope = originalRequest.headers["scope"] || "Productores";

            if (!refreshTokenPromise) {
              refreshTokenPromise = AuthenticationService.getToken(scope)
                .then((tokenResponse) => {
                  const newToken = tokenResponse.data?.access_token;
                  if (newToken) {
                    setTokens((prev) => {
                      const updated = { ...prev, [scope]: tokenResponse.data };
                      tokensRef.current = updated;
                      return updated;
                    });

                    return newToken;
                  }
                  throw new Error("Failed to get a new token");
                })
                .catch((refreshError) => {
                  logout();
                  throw refreshError;
                })
                .finally(() => {
                  refreshTokenPromise = null;
                });
            }

            try {
              const newToken = await refreshTokenPromise;

              if (newToken) {
                originalRequest.headers["Authorization"] = `Bearer ${newToken}`;
                return api(originalRequest);
              }
            } catch (e) {
              return Promise.reject(e);
            }
          }
          return Promise.reject(error);
        }
      );
    } catch (error) {
      logout();
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!initialized.current) {
      initialized.current = true;

      const accessToken = localStorage.getItem("hdi.access_token");
      if (accessToken) {
        fetchTokens();
      } else {
        setIsLoading(false);
        if (window.location.pathname !== "/login") {
          window.location.href = "/login";
        }
      }
    }
  }, []);

  useEffect(() => {
    if (tokens) {
      setIsLoading(false);
    }
  }, [tokens]);

  const authProviderValue = useMemo(
    () => ({
      ...tokens,
      fetchTokens,
      getUserInfo,
      logout,
      login,
      updateUserInfo,
      userInfo,
      isLoadingLogin
    }),
    [tokens, userInfo, isLoadingLogin]
  );

  return (
    <AuthContext.Provider value={authProviderValue}>
      {isLoading ? <Loading /> : <> {children} </>}
    </AuthContext.Provider>
  );
};
